import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { hasPendingWrites } from "@/lib/write-queue";

const CHECK_INTERVAL = 60_000;

// The reload prompt waits until no edit, popover or dialog is open and nothing is saving.
// A suggestion hint stays open until answered, so it does not count.
function isEditing(): boolean {
  return (
    hasPendingWrites() ||
    !!document.querySelector(
      '[data-editing="true"], [data-radix-popper-content-wrapper]:not(:has([data-component="SuggestionHint"])), [role="dialog"]:not([data-component="SuggestionHint"])',
    )
  );
}

export function useVersionCheck() {
  const initialVersion = useRef<string | null>(null);
  const notified = useRef(false);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    async function check() {
      if (notified.current) return;
      try {
        const res = await fetch(`${import.meta.env.BASE_URL}version.json`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const { version, imageTag } = await res.json();
        if (initialVersion.current === null) {
          initialVersion.current = version;
          return;
        }
        if (version !== initialVersion.current) {
          if (isEditing()) return;
          notified.current = true;
          const detectedAt = new Date().toISOString().slice(0, 16).replace("T", " ") + " UTC";
          toast.info("Ny versjon tilgjengelig", {
            description: (
              <>
                Klikk for å oppdatere til siste versjon.
                <br />
                {detectedAt}
                {imageTag && (
                  <>
                    <br />
                    {imageTag}
                  </>
                )}
              </>
            ),
            action: {
              label: "Oppdater",
              onClick: () => window.location.reload(),
            },
            duration: Number.POSITIVE_INFINITY,
          });
        }
      } catch {
        // Network errors are expected when offline
      }
    }

    check();
    const id = setInterval(check, CHECK_INTERVAL);
    return () => clearInterval(id);
  }, []);
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return msg.includes("failed to fetch dynamically imported module") || msg.includes("dynamically imported module");
}

export function DefaultErrorComponent({ error }: ErrorComponentProps) {
  if (isChunkLoadError(error)) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2 style={{ marginBottom: "0.5rem" }}>Ny versjon tilgjengelig</h2>
        <p>Siden må lastes på nytt for å hente siste versjon.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1rem",
            cursor: "pointer",
          }}
        >
          Last inn på nytt
        </button>
      </div>
    );
  }
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h2 style={{ marginBottom: "0.5rem" }}>Noe gikk galt</h2>
      <p>Prøv å laste siden på nytt.</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          marginTop: "1rem",
          padding: "0.5rem 1rem",
          cursor: "pointer",
        }}
      >
        Last inn på nytt
      </button>
    </div>
  );
}
